import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import { useCan } from '@/auth/AuthContext';

const STATE_LABEL = {
  active:       { label: 'Запущен',     cls: 'svc-state--ok' },
  reloading:    { label: 'Перезагрузка', cls: 'svc-state--warn' },
  activating:   { label: 'Запускается', cls: 'svc-state--warn' },
  deactivating: { label: 'Останавлив.', cls: 'svc-state--warn' },
  inactive:     { label: 'Остановлен',  cls: 'svc-state--off' },
  failed:       { label: 'Ошибка',      cls: 'svc-state--err' },
};

function StateBadge({ status }) {
  const m = STATE_LABEL[status?.activeState] || { label: status?.activeState || '?', cls: '' };
  return <span className={'svc-state ' + m.cls}><span className="svc-state__dot" />{m.label}</span>;
}

function fmtSince(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return Math.floor(ms / 1000) + ' сек.';
  if (ms < 3_600_000) return Math.floor(ms / 60_000) + ' мин.';
  if (ms < 86_400_000) return Math.floor(ms / 3_600_000) + ' ч.';
  return Math.floor(ms / 86_400_000) + ' дн.';
}

export default function ServicesPanel() {
  const can = useCan();
  const canControl = can('services.manage', 'control');

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [acting, setActing] = useState(null);

  const loadAll = useCallback(async () => {
    setError(null);
    try {
      const data = await api.get('/api/services');
      setServices(data.rows || []);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось загрузить список сервисов');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Автообновление статусов раз в 5 секунд
  useEffect(() => {
    const t = setInterval(async () => {
      if (acting) return;
      try {
        const data = await api.get('/api/services');
        setServices(data.rows || []);
      } catch { /* тихо игнорируем — увидим при ручном обновлении */ }
    }, 5000);
    return () => clearInterval(t);
  }, [acting]);

  const onControl = async (id, action) => {
    if (!canControl) return;
    if (action === 'stop' && !confirm('Остановить сервис?')) return;
    setActing(`${id}:${action}`);
    setError(null);
    try {
      const res = await api.post(`/api/services/${id}/control`, { action });
      setServices((prev) => prev.map((s) => s.id === id ? { ...s, status: res.status } : s));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : `Не удалось выполнить ${action}`);
    } finally {
      setActing(null);
    }
  };

  if (loading) return <div className="settings-card">Загрузка…</div>;

  return (
    <div className="services-list">
      {error && (
        <div className="settings-card" style={{ marginBottom: 12 }}>
          <div style={{ padding: 16, color: 'var(--red-fg)' }}>Ошибка: {error}</div>
        </div>
      )}
      {services.length === 0 && (
        <div className="settings-card">
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--fg-muted)' }}>
            Нет сервисов в реестре.
          </div>
        </div>
      )}
      {services.map((s) => (
        <ServiceCard
          key={s.id}
          svc={s}
          canControl={canControl}
          acting={acting}
          onControl={onControl}
        />
      ))}
    </div>
  );
}

function ServiceCard({ svc, canControl, acting, onControl }) {
  const { id, label, description, unit, status } = svc;
  const isActing = acting?.startsWith(`${id}:`);
  const installed = status?.installed !== false;

  return (
    <div className="settings-card svc-card">
      <div className="svc-card__head">
        <div>
          <h2 className="settings-card__title">
            {label} <span className="svc-card__unit">{unit}</span>
          </h2>
          {description && <p className="settings-card__sub">{description}</p>}
          {!installed && (
            <p style={{ color: 'var(--red-fg)', fontSize: 12.5, marginTop: 6 }}>
              Юнит не установлен (LoadState={status.loadState || '?'}). См. deploy/SERVICES.md.
            </p>
          )}
        </div>
        <StateBadge status={status} />
      </div>

      <div className="svc-card__meta">
        <Meta label="Подсостояние" value={status?.subState || '—'} />
        <Meta label="Автозапуск"    value={status?.unitFileState || '—'} />
        <Meta label="PID"           value={status?.mainPid ?? '—'} />
        <Meta label="Аптайм"        value={fmtSince(status?.activeSince)} />
      </div>

      <div className="svc-card__actions">
        <button
          className="btn btn--sm"
          disabled={!canControl || !installed || status?.activeState === 'active' || isActing}
          onClick={() => onControl(id, 'start')}
        >
          {acting === `${id}:start` ? 'Запускаем…' : 'Запустить'}
        </button>
        <button
          className="btn btn--sm"
          disabled={!canControl || !installed || status?.activeState === 'inactive' || isActing}
          onClick={() => onControl(id, 'stop')}
        >
          {acting === `${id}:stop` ? 'Останавливаем…' : 'Остановить'}
        </button>
        <button
          className="btn btn--primary btn--sm"
          disabled={!canControl || !installed || isActing}
          onClick={() => onControl(id, 'restart')}
        >
          {acting === `${id}:restart` ? 'Перезапускаем…' : 'Перезапустить'}
        </button>
      </div>

      <LogsBlock id={id} />
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div className="svc-meta">
      <div className="svc-meta__label">{label}</div>
      <div className="svc-meta__value">{value}</div>
    </div>
  );
}

function LogsBlock({ id }) {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [lines, setLines] = useState(200);
  const preRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/services/${id}/logs?lines=${lines}`);
      if (!res.ok) {
        setError(res.message || 'Логи недоступны');
        setText('');
      } else {
        setText(res.text || '');
        // прокрутить вниз после рендера
        setTimeout(() => {
          if (preRef.current) preRef.current.scrollTop = preRef.current.scrollHeight;
        }, 0);
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось загрузить логи');
    } finally {
      setLoading(false);
    }
  }, [id, lines]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  return (
    <div className="svc-logs">
      <div className="svc-logs__head">
        <button className="btn btn--ghost btn--sm" onClick={() => setOpen((o) => !o)}>
          {open ? 'Скрыть логи' : 'Показать логи'}
        </button>
        {open && (
          <>
            <select
              className="settings-field__input"
              style={{ width: 120, height: 28 }}
              value={lines}
              onChange={(e) => setLines(Number(e.target.value))}
            >
              <option value={50}>50 строк</option>
              <option value={200}>200 строк</option>
              <option value={500}>500 строк</option>
              <option value={1000}>1000 строк</option>
            </select>
            <button className="btn btn--ghost btn--sm" onClick={load} disabled={loading}>
              {loading ? 'Обновляем…' : 'Обновить'}
            </button>
          </>
        )}
      </div>
      {open && (
        <>
          {error && <div style={{ color: 'var(--red-fg)', fontSize: 12.5, padding: '0 4px 8px' }}>{error}</div>}
          <pre ref={preRef} className="svc-logs__pre">{text || (loading ? '' : '— нет записей —')}</pre>
        </>
      )}
    </div>
  );
}
